/**
 * Module dependencies.
 */

import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import {
  CrudAddType,
  CrudEditType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  FormList,
  ListColumns
} from 'app/components/organisms/crud/form-types';

import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { apiEndpoints } from 'app/core/config/api-endpoints';
import { requiredRule } from 'app/core/utils/field-rules';
import { appRoutes } from 'app/routes';
import { TFunction } from 'i18next';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns => [
  {
    title: translate('common.table.columns.id'),
    dataIndex: 'id',
    size: '30px',
    key: 'id'
  },
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'name',
    key: 'name'
  },

  {
    title: translate('common.table.columns.phone'),
    style: { minWidth: 150 },
    dataIndex: 'phone',

    key: 'phone'
  },
  {
    title: translate('common.table.columns.email'),
    style: { minWidth: 250 },
    dataIndex: 'email',
    key: 'email'
  },
  {
    title: translate('common.table.columns.notes'),
    dataIndex: 'notes',
    size: '1fr',
    key: 'notes'
  }
];

/**
 * Form list.
 */

const formFields = (translate: TFunction): FormList => [
  {
    type: 'inputField',
    name: 'name',
    label: translate('common.labels.name'),
    rules: requiredRule(translate)
  },
  {
    type: 'inputField',
    name: 'email',
    label: translate('common.labels.email'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'phone',
    label: translate('common.labels.phone'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'notes',
    label: translate('common.labels.notes'),
    rules: undefined
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: String(translate('common.labels.search'))
  }
];

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType => ({
  formFields: formFields(translate)
});

/**
 * Edit.
 */

const edit = (translate: TFunction): CrudEditType => ({
  formFields: formFields(translate)
});

/**
 * Remove.
 */

const remove = (): CrudRemoveType => ({});

/**
 * List.
 */

const list = (translate: TFunction): CrudListType => ({
  columns: listColumns(translate),
  style: { minWidth: 500 },
  nestedParams: item => ({ supplierId: item.id }),
  route: appRoutes.dashboard.projects.project.suppliersContacts,
  key: ['projectSuppliersContacts'],
  endpoint: apiEndpoints.projectSuppliersContacts,
  filters: listFilters(translate)
});

/**
 * Config.
 */

export function createProjectSuppliersContactsTemplate(translate: TFunction): CrudTemplate {
  return normalizeTemplate(translate, {
    add: add(translate),
    edit: edit(translate),
    list: list(translate),
    remove: remove()
  });
}
